import { uid } from 'radash'
import { memo, useCallback, useEffect, useState } from 'react'
import Icon from '~components/ui/icon'
import { NOTION_PAGES } from '~constants'
import Loading from '~containers/general/loading'
import useLogInOTP from '~hooks/samsung.smart-things/use-login-otp'
import useDarkMode from '~hooks/use-dark-mode'
import { isOtpSpent } from '~utils/app/spent-otp'
import Mixpanel from '~utils/mixpanel'

interface LoginProps {
  onSuccess?: () => void
  state?: string
  otp?: string
}
const Login = memo(({ state, otp }: LoginProps) => {
  const isDarkMode = useDarkMode()
  const [isInLoginWithOtp, setIsInLoginWithOtp] = useState(false)

  const { mutate: loginOTP } = useLogInOTP({
    onMutate: () => {
      setIsInLoginWithOtp(true)
    },
    onSettled: () => {
      setIsInLoginWithOtp(false)
    },
  })

  const onSamsungAccountLoginStart = useCallback(() => {
    const baseUrl = `${import.meta.env.VITE_SAMSUNG_AUTHORIZATION_HOST_URL}/accounts/v1/${import.meta.env.VITE_SAMSUNG_APP_ALIAS_ID}/signInGate`
    const uuid = uid(32)
    const url = new URL(baseUrl)
    url.searchParams.set('redirect_uri', import.meta.env.VITE_SAMSUNG_APP_REDIRECT_URI)
    url.searchParams.set('state', state || uuid)
    url.searchParams.set('client_id', import.meta.env.VITE_SAMSUNG_APP_CLIENT_ID)
    url.searchParams.set('response_type', 'code')
    url.searchParams.set('scope', 'offline.access')
    window.location.href = url.toString()
  }, [state])

  useEffect(() => {
    // DRT-6525: 이미 소비한 OTP 는 서버가 지웠으므로 재시도해도 401 뿐이고, 실패가
    // 쌓이면 브루트포스 카운터(DRT-5809)만 IP 단위로 올린다. 시도 자체를 하지 않는다.
    if (otp && !isOtpSpent(otp)) {
      loginOTP({ otp })
    }
    Mixpanel.logEvent('product: visited', {
      is_user_scenario: true,
    })
  }, [otp, loginOTP])

  if (isInLoginWithOtp) {
    return <Loading />
  }

  return (
    <main className="flex flex-1 flex-col items-center justify-center h-full py-11">
      <div className="py-5 w-full h-[68px] flex justify-center items-center">
        <Icon name="drtail_text_logo" className="h-7 w-[100px] fill-primary-600 dark:fill-white" />
      </div>
      <section className="flex flex-col items-center w-full flex-1 justify-between min-h-[400px]">
        <section>
          <h1 className="mb-4 px-5 py-2 text-center text-primary-600 text-title-3 dark:text-gray-300">
            Log in / Sign up
          </h1>
          <div>
            <div className="flex flex-col justify-between items-center w-full py-2 px-5 flex-gap-y-4 mt-8">
              <button
                className="w-[200px] h-[54px] bg-white dark:bg-primary-600 rounded-[28px] flex items-center justify-center flex-gap-x-2 shadow-lg hover:shadow-xl transition-shadow hover:cursor-pointer"
                onClick={onSamsungAccountLoginStart}
              >
                <Icon
                  name={isDarkMode ? 'samsung_account_dark' : 'samsung_account'}
                  className="size-6"
                />
                <span className="text-primary-600 text-title-3 dark:text-white">
                  Samsung Account
                </span>
              </button>
            </div>
            <p className="text-center text-body-3 xs:text-body-2 text-primary-600 whitespace-pre-line w-full px-5 mt-10 dark:text-gray-500">
              {`To connect Dr.Tail to your Pet Care services,\nlog in using your Samsung account.`}
            </p>
          </div>
        </section>
        <div className="px-5 mt-20">
          <p className="text-body-3 xs:text-body-2 text-gray-700 whitespace-pre-wrap min-h-[120px] max-w-sm dark:text-gray-700">
            By continuing, you agree to our{' '}
            <a href={NOTION_PAGES.TERMS_OF_SERVICE.appUri} target="_blank" className="underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href={NOTION_PAGES.PRIVACY_POLICY.appUri} target="_blank" className="underline">
              Privacy Policy
            </a>
            . To enhance your experience, we’ll send you SMS messages. If you’d like to stop
            receiving Dr.Tail SMS, simply reply STOP.
          </p>
        </div>
      </section>
    </main>
  )
})

Login.displayName = 'Login'
export default Login
